import { useCallback, useMemo } from 'react';

import IP from 'constants/form/inputParser.const';
import useTip from 'hooks/useTip.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import parseMoney from 'utils/parsers/parseMoney.util';
import parseText from 'utils/parsers/parseText.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import isFn from 'utils/predicates/function/isFn.util';

import useResettingState from './useResettingState.hook';

const PARSER = Object.freeze({
  [IP.text]: parseText,
  [IP.money]: parseMoney,
});

// NOTE: this little JSDoc snippet seems to stop WebStorm adding a very long inlay hint
/** @return Helpers */

const useInputHelpers = (parser, value, name, tag) => {
  tag = fromAnyToTrimmed(tag); // eslint-disable-line no-param-reassign
  const [localValue, setLocalValue] = useResettingState(value);
  const { val: isDirty, inv: isClean, set: setDirty } = useTip(false);
  const { val: isError, inv: isValid, set: setError } = useTip(false);
  const { val: isFocus, yay: receivedFocus, nay: lostFocus } = useTip(false);

  const prepare = useCallback(
    (ev, ...extra) => {
      const raw = fromEventToValue(ev);
      const dirty = raw !== value;
      const parse = isFn(parser) ? parser : PARSER[parser];
      const parsed = isFn(parse) ? parse({ value: raw, extra }) : { raw, is: {} };

      return { ...parsed, tag, parser, name, dirty, ev, event: ev };
    },
    [name, parser, tag, value],
  );

  const reconcile = useCallback(
    ({ result, prepared }) => {
      const { error, value, dirty } = asObject(result);

      const newValue = isMissing(value) ? prepared.raw : value;
      const newDirty = isMissing(dirty) ? newValue !== value : dirty;
      const newError = error || null;

      setLocalValue(newValue);
      setDirty(newDirty);
      setError(newError);

      return { value: newValue, dirty: newDirty, error: newError };
    },
    [setDirty, setError, setLocalValue],
  );

  const handle = useCallback(
    (name, fn, ev, ...extra) => {
      const prepared = prepare(ev, ...extra);
      const result = call(name, fn, prepared);

      return reconcile({ result, prepared });
    },
    [prepare, reconcile],
  );

  return useMemo(
    () => ({
      handle,
      tag,
      value: localValue,
      attributes: {
        name,
        'data-tag': tag,
        'data-f-clean': isClean,
        'data-f-dirty': isDirty,
        'data-f-error': isError,
        'data-f-focus': isFocus,
        'data-f-valid': isValid,
      },
      focus: {
        received: receivedFocus,
        lost: lostFocus,
      },
      is: {
        clean: isClean,
        dirty: isDirty,
        error: isError,
        focus: isFocus,
        valid: isValid,
      },
    }),
    [handle, tag, localValue, name, isClean, isDirty, isError, isFocus, isValid, receivedFocus, lostFocus],
  );
};

export default useInputHelpers;
